export type LineChange = "added" | "removed" | "unchanged";

export interface DiffLine {
  text: string;
  change: LineChange;
}

/** A comparison matrix beyond this size falls back to a whole-document change. */
const maxDiffCells = 1_000_000;

/**
 * The line-level diff between two versions of one artefact body. Common leading and
 * trailing lines are trimmed first, so only the changed middle is compared. The rows
 * read in the order of the newer document, with removed lines kept beside the line
 * that replaced them.
 */
export function diffArtifactBody(older: string, newer: string): DiffLine[] {
  const oldLines = older.split("\n");
  const newLines = newer.split("\n");
  let start = 0;
  while (
    start < oldLines.length &&
    start < newLines.length &&
    oldLines[start] === newLines[start]
  ) {
    start += 1;
  }
  let oldEnd = oldLines.length;
  let newEnd = newLines.length;
  while (oldEnd > start && newEnd > start && oldLines[oldEnd - 1] === newLines[newEnd - 1]) {
    oldEnd -= 1;
    newEnd -= 1;
  }
  const head = newLines.slice(0, start).map((text) => ({ text, change: "unchanged" as const }));
  const tail = newLines.slice(newEnd).map((text) => ({ text, change: "unchanged" as const }));
  const oldMiddle = oldLines.slice(start, oldEnd);
  const newMiddle = newLines.slice(start, newEnd);
  if ((oldMiddle.length + 1) * (newMiddle.length + 1) > maxDiffCells) {
    return [
      ...head,
      ...oldMiddle.map((text) => ({ text, change: "removed" as const })),
      ...newMiddle.map((text) => ({ text, change: "added" as const })),
      ...tail,
    ];
  }
  return [...head, ...lcsDiff(oldMiddle, newMiddle), ...tail];
}

/**
 * The longest common subsequence of two line lists, read as diff rows. A removed line
 * sits next to the line that took its place, so the reader sees the change in place.
 */
function lcsDiff(older: string[], newer: string[]): DiffLine[] {
  const rows = older.length + 1;
  const columns = newer.length + 1;
  const table: number[][] = Array.from({ length: rows }, () => new Array<number>(columns).fill(0));
  for (let i = older.length - 1; i >= 0; i -= 1) {
    for (let j = newer.length - 1; j >= 0; j -= 1) {
      table[i]![j] =
        older[i] === newer[j]
          ? table[i + 1]![j + 1]! + 1
          : Math.max(table[i + 1]![j]!, table[i]![j + 1]!);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < older.length && j < newer.length) {
    if (older[i] === newer[j]) {
      out.push({ text: newer[j]!, change: "unchanged" });
      i += 1;
      j += 1;
    } else if (table[i + 1]![j]! >= table[i]![j + 1]!) {
      out.push({ text: older[i]!, change: "removed" });
      i += 1;
    } else {
      out.push({ text: newer[j]!, change: "added" });
      j += 1;
    }
  }
  while (i < older.length) {
    out.push({ text: older[i]!, change: "removed" });
    i += 1;
  }
  while (j < newer.length) {
    out.push({ text: newer[j]!, change: "added" });
    j += 1;
  }
  return out;
}
